import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from 'html5-qrcode'

// Connects to data-controller="validation-presential"
export default class extends Controller {
  static targets = ["scanButton", "statusCamera", "input"]

  connect() {
    this.html5QrCode = null
    this.currentStream = null

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.stopScanner()
    })

    document.addEventListener("turbo:before-cache", () => {
      this.stopScanner()
    })

    if (this.inputTarget && this.inputTarget.form) {
      this.inputTarget.form.addEventListener("submit", () => {
        this.inputTarget.value = this.inputTarget.value.trim()
      })
    }

    const orderForm = document.querySelector('form[action*="validator"]')
    if (orderForm) {
      orderForm.addEventListener('ajax:success', (event) => {
        const [data] = event.detail
        if (data.success) {
          document.querySelector("#qr-codes-table").innerHTML = data.html
          this.updateStatus("Orden Validada", true)
          // Limpiar el input después de validación exitosa
          if (this.hasInputTarget) {
            this.inputTarget.value = ""
          }
        } else {
          this.updateStatus(`Error: ${data.error || 'Error desconocido'}`, false)
          if (this.hasInputTarget) {
            this.inputTarget.value = ""
          }
        }
      })
    }
  }

  disconnect() {
    clearTimeout(this._statusTimeout)
    this.stopScanner()
  }

  scan() {
    const storeId = this.scanButtonTarget.dataset.storeId

    const readerEl = document.getElementById("qr-reader")
    const containerWidth = readerEl ? readerEl.clientWidth : window.innerWidth
    const qrboxSide = Math.floor(containerWidth * 0.75)
    const qrboxSize = { width: qrboxSide, height: qrboxSide }

    if (!this.html5QrCode) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const hasCamera = devices.some(device => device.kind === 'videoinput')
        if (hasCamera) {
          this.startScanner(qrboxSize, storeId)
        } else {
          this.requestCameraPermission(qrboxSize, storeId)
        }
      })
    } else {
      this.stopScanner()
    }
  }

  requestCameraPermission(qrboxSize, storeId, partnerName) {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        stream.getTracks().forEach(track => track.stop())
        this.startScanner(qrboxSize, storeId)
      })
      .catch(() => {
        this.statusCameraTarget.innerHTML = this.cameraErrorMessage()
        this.statusCameraTarget.classList.remove('d-none')
        setTimeout(() => {
          this.statusCameraTarget.classList.add('d-none')
        }, 5000)
      })
  }

  startScanner(qrboxSize, storeId) {
    this.html5QrCode = new Html5Qrcode("qr-reader")
    this.html5QrCode.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: qrboxSize },
      (decodedText) => {
        this.onScanSuccess(decodedText, storeId)
        this.stopScanner()
      },
      (errorMessage) => {
        console.log("Error en el escaneo: ", errorMessage)
      }
    ).then(() => {
      this.scanButtonTarget.textContent = "Detener Escáner"
    }).catch(err => {
      console.error("Error al iniciar el escáner", err)
      this.statusCameraTarget.innerHTML = this.cameraErrorMessage()
      this.scanButtonTarget.textContent = "Escanear QR"
      this.html5QrCode = null
    })
  }

  stopScanner() {
    if (this.html5QrCode) {
      const scanner = this.html5QrCode
      this.html5QrCode = null
      this.scanButtonTarget.textContent = "Escanear QR"

      const cleanup = () => {
        try { scanner.clear() } catch (_e) { /* element already removed */ }
        if (this.currentStream) {
          this.currentStream.getTracks().forEach(track => track.stop())
          this.currentStream = null
        }
      }

      if (scanner.isScanning) {
        scanner.stop().then(cleanup).catch((_err) => {
          cleanup()
        })
      } else {
        cleanup()
      }
    }
  }

  onScanSuccess(decodedText, storeId) {
    let qrData
    try {
      qrData = JSON.parse(decodedText)
    } catch (e) {
      console.error("Error al parsear el texto decodificado:", e)
      this.updateStatus("QR inválido", false)
      return
    }

    if (typeof qrData.token !== "string" || !qrData.token.trim()) {
      this.updateStatus("QR inválido", false)
      return
    }

    fetch(`/stores/${storeId}/validator`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ qr_token: qrData.token })
    })
      .then(response => this.parseJsonResponse(response))
      .then(data => {
        if (data.success) {
          document.querySelector("#qr-codes-table").innerHTML = data.html
          this.updateStatus("Orden Validada 🤘🏼", true)
        } else {
          this.updateStatus(`Error: ${data.error || 'Error desconocido'}`, false)
        }
      })
      .catch(error => {
        console.error("Error en la solicitud:", error)
        this.updateStatus(error.message || "No se pudo validar el QR", false)
      })
  }

  async parseJsonResponse(response) {
    const contentType = response.headers.get("content-type") || ""

    if (!contentType.includes("application/json")) {
      const bodyPreview = await response.text().then(text => text.slice(0, 200)).catch(() => "")
      console.error("Respuesta no JSON en validador QR", {
        status: response.status,
        contentType,
        bodyPreview
      })
      throw new Error("No se pudo leer la respuesta del servidor. Recarga la página e intenta nuevamente.")
    }

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || data.message || "No se pudo completar la solicitud.")
    }

    return data
  }

  updateStatus(message, success = true) {
    const status = this.statusCameraTarget
    const icon = success ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'
    const alertClass = success ? 'alert-success' : 'alert-danger'

    status.classList.remove("d-none")
    status.className = `status-toast dashboard-alert ${alertClass}`
    status.innerHTML = `
      <i class="bi ${icon}"></i>
      <span>${message}</span>
    `

    clearTimeout(this._statusTimeout)
    this._statusTimeout = setTimeout(() => {
      status.classList.add("d-none")
      status.className = ""
      status.innerHTML = ""
    }, 5000)
  }

  cameraErrorMessage() {
    return `
      <div class="dashboard-alert alert-warning" role="alert">
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>No se encontró cámara disponible</span>
      </div>`
  }

  // Confirm payment cash and send to kitchen
  confirmPayment(event) {
    const button = event.currentTarget
    const orderId = button.dataset.orderId
    const storeId = button.dataset.storeId

    button.disabled = true
    button.innerHTML = '<i class="bi bi-hourglass-split"></i> Procesando...'

    fetch(`/stores/${storeId}/confirm_payment/${orderId}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      }
    })
      .then(response => this.parseJsonResponse(response))
      .then(data => {
        if (data.success) {
          this.updateStatus("Pago Confirmado", true)

          // Actualizar el botón a "Enviado" y mantener deshabilitado
          button.classList.remove('btn-primary-dashboard')
          button.classList.add('btn-success-sent')
          button.innerHTML = '<i class="bi bi-check-circle"></i> Pago confirmado'
          button.disabled = true

          // Actualizar el badge de estado
          const orderCard = document.getElementById(`order-${orderId}`)
          if (orderCard) {
            const badge = orderCard.querySelector('.badge-status')
            if (badge) {
              badge.classList.remove('badge-warning')
              badge.classList.add('badge-success')
              badge.innerHTML = '<i class="bi bi-check-circle"></i> pagado'
            }
          }
        } else {
          this.updateStatus(`Error: ${data.error}`, false)
          button.disabled = false
          button.innerHTML = '<i class="bi bi-cash-coin"></i> Confirmar pago y enviar a cocina'
        }
      })
      .catch(error => {
        console.error("Error:", error)
        this.updateStatus(error.message || "No se pudo confirmar el pago", false)
        button.disabled = false
        button.innerHTML = '<i class="bi bi-cash-coin"></i> Confirmar pago y enviar a cocina'
      })
  }
}
